import appCss from '~/styles/app.css?url'

import {ReactQueryDevtools} from '@tanstack/react-query-devtools'
import {TanStackRouterDevtools} from '@tanstack/router-devtools'
import {createRootRoute, Outlet} from '@tanstack/react-router'
import {ErrorBoundary} from 'react-error-boundary'
import {QueryClient} from '@tanstack/react-query'
import {Meta, Scripts} from '@tanstack/start'
import {ReactNode, Suspense} from 'react'
import {v7 as uuid} from 'uuid'
import useLocaleParams from '~/hooks/useLocaleParams'
import useShopConfig from '~/hooks/useShopConfig'
import {shopQueryOptions} from '~/client/fn/shop'
import NotFound from '~/components/NotFound'
import Error from '~/components/Error'

export const Route = createRootRoute({
    head: () => ({
        meta: [
            {
                title: 'Gravity Shirts',
            },
            {
                charSet: 'utf-8',
            },
            {
                name: 'viewport',
                content: 'width=device-width, initial-scale=1',
            },
            {
                name: 'theme-color',
                content: '#101015',
            },
            {
                name: 'color-scheme',
                content: 'dark',
            },
        ],
        links: [
            {
                rel: 'apple-touch-icon',
                sizes: '180x180',
                href: '/favicon/apple-touch-icon.png',
            },
            {
                rel: 'icon',
                type: 'image/png',
                sizes: '32x32',
                href: '/favicon/favicon-32x32.png',
            },
            {
                rel: 'icon',
                type: 'image/png',
                sizes: '16x16',
                href: '/favicon/favicon-16x16.png',
            },
            {
                rel: 'manifest',
                href: '/favicon/site.webmanifest',
            },
            {
                rel: 'stylesheet',
                href: appCss,
            },
            {
                rel: 'preload',
                href: appCss,
                as: 'style',
            },
        ],
    }),
    component: RootComponent,
    notFoundComponent: NotFoundComponent,
    errorComponent: ErrorComponent,
    headers: ({loaderData}) => {
        // @ts-ignore
        const {isDevelopment, isBot} = loaderData
        return {
            ...(!isBot
                ? {
                      'Cache-Control': 's-maxage=3600, stale-while-revalidate=3600',
                  }
                : {}),
            ...(!isDevelopment
                ? {
                      'Content-Security-Policy': `default-src 'self'; style-src 'self' data: 'unsafe-inline'; script-src 'self' 'unsafe-inline'; img-src 'self' data: https://image.spreadshirtmedia.net https://cdn.sanity.io/images/; frame-ancestors 'self'; frame-src 'self'`,
                  }
                : {}),
        }
    },
    context: ({context}) => {
        return {
            ...context,
            // @ts-ignore
            queryClient: context.queryClient as QueryClient,
        }
    },
    loader: async ({context}) => {
        const nonce = uuid()
        const shop = await context.queryClient.ensureQueryData(shopQueryOptions())
        return {...shop, nonce}
    },
})

function RootComponent() {
    return (
        <RootDocument>
            <Outlet />
        </RootDocument>
    )
}

function NotFoundComponent() {
    return (
        <RootDocument>
            <NotFound />
        </RootDocument>
    )
}

function ErrorComponent() {
    return (
        <RootDocument>
            <Error />
        </RootDocument>
    )
}

function RootDocument({children}: {children: ReactNode}) {
    const shopConfig = useShopConfig()
    const {lang} = useLocaleParams()
    // noinspection HtmlRequiredTitleElement
    return (
        <html lang={lang ?? 'en'}>
            <head>
                <Meta />
            </head>
            <body>
                <Suspense fallback={null}>
                    <ErrorBoundary fallback={<Error />}>{children}</ErrorBoundary>
                </Suspense>
                {shopConfig.isDevelopment && (
                    <>
                        <TanStackRouterDevtools position="bottom-right" />
                        <ReactQueryDevtools buttonPosition="bottom-left" />
                    </>
                )}
                <Scripts />
            </body>
        </html>
    )
}
